import React, { useEffect, useRef, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import cardValidator from 'card-validator';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';
import { hashHistory } from 'react-router';

import { paymentApiServices } from '../../../weborder/services';
import { Button, Snackbar, TextField, Typography } from '../../atoms';
import { Tick as TickIcon } from '../../icons';
import { Dialog } from '../../molecules';
import { getCardIcon, getIsHTTPS } from './tools/CreditCardFormUtils';
import { CreditCardFormProps as Props } from './types';

type FormInputs = {
  ccNumber: string;
  cvvNumber: string;
  expDate: string;
};

const isHttps = getIsHTTPS();

const useStyles = makeStyles((theme: Theme) => ({
  creditCardDialog: {
    '& .MuiPaper-root': {
      minHeight: '440px ',
    },
    '& .MuiFormControl-root': {
      marginBottom: theme.spacing(2.5),
    },
  },
  title: {
    paddingBottom: theme.spacing(2.5),
  },
  valid: {
    color: theme.palette.success.main,
  },
  wideSpacing: {
    wordSpacing: theme.spacing(1),
  },
  inputLeft: {},
  inputRight: {
    padding: 0,
  },
  secondRow: {
    '& .MuiFormControl-root': {
      boxSizing: 'border-box',
      width: '50%',

      '&:first-child': {
        paddingRight: theme.spacing(1),
      },
      '&:last-child': {
        paddingLeft: theme.spacing(1),
      },
    },
  },
}));

export const AddCreditCardDialog = (props: Props): JSX.Element => {
  const {
    cancelAddCardBridge,
    creditCardPaymentAdded,
    isFromCheckoutPage,
    onFail,
    onSuccess,
    translate,
  } = props;

  const { errors, formState, handleSubmit, register } = useForm<FormInputs>({
    validateCriteriaMode: 'all',
  });

  // Read the formState before render to subscribe the form state through Proxy
  const { isSubmitting } = formState;

  const [card, setCard] = useState<cardValidator.Card>();
  const [validNumber, setValidNumber] = useState<cardValidator.validNumber>();
  const [submitError, setSubmitError] = useState<Error>();
  const [formattedCcNumber, setFormattedCcNumber] = useState('');
  const [formattedExpDate, setFormattedExpDate] = useState('');
  const [formattedCvvNumber, setFormattedCvvNumber] = useState('');

  const classes = useStyles({});
  const isCancelFlag = useRef(true);

  useEffect(() => {
    return () => {
      if (isCancelFlag.current) {
        cancelAddCardBridge();
      }
    };
  }, []);

  const onSubmit = handleSubmit(async (props) => {
    try {
      // TODO LOCK ROUTER
      const { month, year } = cardValidator.expirationDate(props.expDate);

      const digitMonth = parseInt(month);
      const fourDigitYear =
        year.length === 4 ? parseInt(year) : parseInt(`20${year}`);

      const paymentAccountId = await paymentApiServices.createCard({
        cardNumber: props.ccNumber,
        expMonth: digitMonth,
        expYear: fourDigitYear,
        cvv: props.cvvNumber,
      });

      isCancelFlag.current = false;

      creditCardPaymentAdded({
        cardNumber: props.ccNumber,
        paymentAccountId,
      });

      onSuccess && onSuccess();
    } catch (error) {
      setSubmitError(error);
      onFail && onFail();
    } finally {
      // TODO UNLOCK ROUTER
    }
  });

  const validateCreditCard = (value): boolean => {
    const result = cardValidator.number(value);
    setFormattedCcNumber(value);
    return result.isValid;
  };
  const validateExpiryDate = (value): boolean => {
    const result = cardValidator.expirationDate(value);
    setFormattedExpDate(value);
    return result.isValid;
  };
  const validateCVV = (value): boolean => {
    const maxLen = validNumber?.card?.code?.size;
    const result = cardValidator.cvv(value, maxLen);
    setFormattedCvvNumber(value);
    return result.isValid;
  };

  const handleOnChangeCardNumber = (event) => {
    const v = cardValidator.number(event.target.value);
    setValidNumber(v);
    setCard(v.card);

    const value: string = event.target.value.replace(/\D/g, '');
    if (v.card) {
      const formatted = Array.from(value)
        // insert spaces where r gaps
        .reduce((agg, c, idx) => {
          if (v.card.gaps.includes(idx)) {
            return `${agg} ${c}`;
          }
          return `${agg}${c}`;
        }, '')
        // don't allow input more than cards max length + spaces
        .slice(0, Math.max(...v.card.lengths) + v.card.gaps.length);

      setFormattedCcNumber(formatted);
    } else {
      setFormattedCcNumber(value);
    }
  };
  const handleOnChangeCvvNumber = (event) => {
    const inputCvv = event.target.value;
    const cvv =
      cardValidator.cvv(inputCvv) &&
      inputCvv
        .replace(/\D/g, '')
        .trim()
        .slice(0, 4);
    setFormattedCvvNumber(cvv);
  };
  const handleOnChangeExpiry = (event) => {
    const expiryValue: string = event.target.value.replace(/\D/g, '');
    const formatted = Array.from(expiryValue)
      .reduce((agg, c, idx) => (idx === 2 ? `${agg}/${c}` : `${agg}${c}`), '')
      .slice(0, 7); // MM/YYYY only

    setFormattedExpDate(formatted);
  };
  const handleOnBackLink = () => {
    hashHistory.goBack();
  };

  const fieldStatus = isHttps ? 'default' : 'disabled';

  const isSubmitDisabled =
    !isHttps || (!!Object.keys(errors).length && !validNumber?.isValid);

  const formStatus = isSubmitting
    ? 'loading'
    : isSubmitDisabled
    ? 'disabled'
    : 'default';

  const dialogTitleKey = isFromCheckoutPage
    ? 'AddNewCard_field_add_card_and_pay'
    : 'AddNewCard_field_add_card';

  const dialogActions = (
    <Button fullWidth onClick={onSubmit} status={formStatus} type="submit">
      {translate(dialogTitleKey)}
    </Button>
  );

  const addCareditCardDialogStyles = clsx(
    'fd-partial__add-credit-card',
    classes.creditCardDialog,
  );

  return (
    <Dialog
      actions={dialogActions}
      className={addCareditCardDialogStyles}
      onClose={handleOnBackLink}
      onTitleClick={handleOnBackLink}
      open={true}
    >
      <header>
        <Typography className={classes.title} variant="h1">
          {translate('AddNewCard_field_add_a_new_card')}
        </Typography>
      </header>

      {!isHttps && (
        <Snackbar
          message={translate('Cards_cannot_added_SSL')}
          severity="warning"
        />
      )}

      <form noValidate>
        <TextField
          autoComplete="cc-number"
          autoFocus
          data-fd="credit-card-number-field"
          endIcon={
            validNumber?.isValid && (
              <span data-fd="credit-card-valid">
                <TickIcon className={classes.valid} />
              </span>
            )
          }
          error={!!errors.ccNumber}
          fullWidth
          helperText={
            errors?.ccNumber &&
            translate('AddNewCard_Warning_Invalid_card_number')
          }
          id="creditCard"
          inputProps={{
            className: classes.wideSpacing,
            inputMode: 'numeric',
            pattern: '[0-9]*',
          }}
          inputRef={register({
            validate: validateCreditCard,
          })}
          label={translate('AddNewCard_field_card_number')}
          name="ccNumber"
          onChange={handleOnChangeCardNumber}
          startIcon={getCardIcon(card)}
          status={fieldStatus}
          value={formattedCcNumber}
        />

        <div className={classes.secondRow}>
          <TextField
            autoComplete="cc-exp"
            data-fd="credit-card-expiry-field"
            error={!!errors.expDate}
            fullWidth
            helperText={
              errors?.expDate && translate('AddNewCard_Warning_Invalid_date')
            }
            id="expiryDate"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            inputRef={register({
              validate: validateExpiryDate,
            })}
            label={translate('AddNewCard_field_exp_date')}
            name="expDate"
            onChange={handleOnChangeExpiry}
            placeholder="MM/YY"
            status={fieldStatus}
            value={formattedExpDate}
          />

          <TextField
            autoComplete="cc-csc"
            data-fd="credit-card-cvv-field"
            error={!!errors.cvvNumber}
            fullWidth
            helperText={
              errors?.cvvNumber && translate('AddNewCard_Warning_InvalidCVC')
            }
            id="cvvNumber"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            inputRef={register({
              validate: validateCVV,
            })}
            label={translate('CVC_number')}
            onChange={handleOnChangeCvvNumber}
            name="cvvNumber"
            status={fieldStatus}
            value={formattedCvvNumber}
          />
        </div>

        {submitError && (
          <Snackbar message={submitError.message} severity="error" />
        )}
      </form>
    </Dialog>
  );
};
